import { AbstractClient, CompositionClient } from "@/app/client";
import {
  PageOptions,
  PreOrder,
  PreOrderList,
  PreOrderUpdate,
  BulkLineConfirmRequest,
  LineCancelRequest,
  LineUpdateRequest,
  BatchFetchRequest,
  BatchUpdateRequest,
  BatchUpdateResponse,
} from "@/app/types";

const FLOW_DECIMAL_FIELDS = ["quantity", "weight", "price_per_weight"] as const;

function normalizeFlowDecimals<T extends Record<string, any>>(preOrder: T): T {
  const flows = preOrder.flows ?? preOrder.pre_order_flows;
  if (!Array.isArray(flows)) return preOrder;
  for (const flow of flows) {
    for (const field of FLOW_DECIMAL_FIELDS) {
      if (flow[field] != null) {
        flow[field] = Number(flow[field]);
      }
    }
  }
  return preOrder;
}

export default class APIPreOrdersSub extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  // Get all pre-orders for the current user
  getAllPreOrders = async (pageOptions: PageOptions): Promise<PreOrderList> => {
    let res = await this._fetch({
      url: "/pre-orders/", // Trailing slash to match backend routing
      method: "GET",
      params: pageOptions,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getPreOrdersByChat = async (
    chatId: string,
    pageOptions: PageOptions,
  ): Promise<PreOrderList> => {
    let res = await this._fetch({
      url: `/pre-orders/source/whatsapp/chat/${encodeURIComponent(chatId)}`,
      method: "GET",
      params: pageOptions,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getPreOrdersByChatsBatch = async (
    chatIds: string[],
  ): Promise<Record<string, PreOrder[]>> => {
    let res = await this._fetch({
      url: "/pre-orders/source/whatsapp/chat/batch",
      method: "POST",
      body: chatIds,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getPreOrdersByDate = async (date: string): Promise<PreOrderList> => {
    let res = await this._fetch({
      url: `/pre-orders/date/${date}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Optimized endpoint that returns pre-orders with embedded flows
  getPreOrdersRecap = async (
    date: string,
    dateField: "delivery" | "order" | "all" = "delivery",
    includeStocks: boolean = false,
    includePartnerAccounts: boolean = false,
    start?: string,
    end?: string,
    openEnded?: boolean,
    endDate?: string,
    modifiedSince?: string,
    includeDeleted?: boolean,
  ): Promise<{
    pre_orders: any[];
    partner_accounts: Record<string, any>;
    current_ids?: string[];
    server_time: string;
  }> => {
    const params: Record<string, any> = {
      date_field: dateField,
      include_stocks: includeStocks,
      include_partner_accounts: includePartnerAccounts,
    };
    if (start !== undefined) params.start = start;
    if (end !== undefined) params.end = end;
    if (openEnded) params.open_ended = true;
    if (endDate) params.end_date = endDate;
    if (modifiedSince) params.modified_since = modifiedSince;
    if (includeDeleted) params.include_deleted = true;

    let res = await this._fetch({
      url: `/pre-orders/recap/${date}`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      const data = await res.json();
      data.pre_orders?.forEach(normalizeFlowDecimals);
      return data;
    }
    throw new Error("Bad content type");
  };

  getPreOrder = async (
    preOrderId: string,
    options?: { includeDeleted?: boolean },
  ): Promise<PreOrder> => {
    const params: Record<string, any> = {};
    if (options?.includeDeleted) params.include_deleted = true;
    let res = await this._fetch({
      url: `/pre-orders/${preOrderId}`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getEmailPreOrders = async (
    pageOptions: PageOptions,
  ): Promise<PreOrderList> => {
    let res = await this._fetch({
      url: "/pre-orders/source/email",
      method: "GET",
      params: pageOptions,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getSMSPreOrders = async (
    phoneNumber: string,
    accountId: string,
    pageOptions: PageOptions,
  ): Promise<PreOrderList> => {
    let res = await this._fetch({
      url: `/pre-orders/source/sms/${encodeURIComponent(
        phoneNumber,
      )}/${accountId}`,
      method: "GET",
      params: pageOptions,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getSMSPreOrdersByChatId = async (
    chatId: string,
    pageOptions: PageOptions,
  ): Promise<PreOrderList> => {
    let res = await this._fetch({
      url: `/pre-orders/sms-chat/${chatId}`,
      method: "GET",
      params: pageOptions,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getSMSPreOrdersByChatsBatch = async (
    chatIds: string[],
  ): Promise<Record<string, PreOrder[]>> => {
    let res = await this._fetch({
      url: "/pre-orders/source/sms/chat/batch",
      method: "POST",
      body: chatIds,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Legacy single-email pre-orders — kept only for `recap/mobile/_legacy`.
  // New code uses the V2 `findByConversation({ emailId })` path.
  getEmailPreOrdersByEmailId = async (
    emailId: string,
    pageOptions: PageOptions,
  ): Promise<PreOrderList> => {
    let res = await this._fetch({
      url: `/pre-orders/email/${emailId}`,
      method: "GET",
      params: pageOptions,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      const data = await res.json();
      return data;
    }
    throw new Error("Bad content type");
  };

  updateStatus = async (
    preOrderId: string,
    status: number,
  ): Promise<PreOrder> => {
    let res = await this._fetch({
      method: "PUT",
      url: `/pre-orders/${preOrderId}/status`,
      auth: ["OAuth2PasswordBearer"],
      bodyMime: "application/json",
      body: { status },
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  putPreOrderId = async (
    preOrderId: string,
    { ...body }: PreOrderUpdate,
  ): Promise<PreOrder> => {
    let res = await this._fetch({
      url: `/pre-orders/${preOrderId}`,
      method: "PUT",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Line management methods
  confirmLine = async (
    preOrderId: string,
    lineId: string,
    partnerId?: string,
  ): Promise<PreOrder> => {
    let res = await this._fetch({
      url: `/pre-orders/${preOrderId}/lines/${lineId}/confirm`,
      method: "POST",
      params: partnerId ? { partner_id: partnerId } : undefined,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  cancelLine = async (
    preOrderId: string,
    lineId: string,
    request: LineCancelRequest,
  ): Promise<PreOrder> => {
    let res = await this._fetch({
      url: `/pre-orders/${preOrderId}/lines/${lineId}/cancel`,
      method: "POST",
      body: request,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  updateLine = async (
    preOrderId: string,
    lineId: string,
    request: LineUpdateRequest,
  ): Promise<PreOrder> => {
    let res = await this._fetch({
      url: `/pre-orders/${preOrderId}/lines/${lineId}`,
      method: "PUT",
      body: request,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  bulkConfirmLines = async (
    request: BulkLineConfirmRequest,
  ): Promise<PreOrder[]> => {
    let res = await this._fetch({
      url: `/pre-orders/lines/bulk-confirm`,
      method: "POST",
      body: request,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getPreOrdersByOrder = async (orderId: string): Promise<PreOrderList> => {
    let res = await this._fetch({
      url: `/pre-orders/order/${orderId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Pre-order flows methods
  getPreOrderFlows = async (preOrderId: string): Promise<any> => {
    let res = await this._fetch({
      url: `/pre-orders/${preOrderId}/flows`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  createPreOrderFlow = async (preOrderId: string, flow: any): Promise<any> => {
    let res = await this._fetch({
      url: `/pre-orders/${preOrderId}/flows`,
      method: "POST",
      body: flow,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  updatePreOrderFlow = async (
    preOrderId: string,
    flowId: string,
    data: any,
  ): Promise<any> => {
    let res = await this._fetch({
      url: `/pre-orders/${preOrderId}/flows/${flowId}`,
      method: "PUT",
      body: data,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  deletePreOrderFlow = async (
    preOrderId: string,
    flowId: string,
  ): Promise<void> => {
    await this._fetch({
      url: `/pre-orders/${preOrderId}/flows/${flowId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
  };

  // Get the maximum pre_order_number for the current company
  // Used for client-side number generation
  getMaxPreOrderNumber = async (): Promise<{ max_number: number }> => {
    let res = await this._fetch({
      url: "/pre-orders/max-number",
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get pre-order number state for robust client-side generation
  // Returns max from DB and user's last synced value
  getNumberState = async (): Promise<{
    max_number: number;
    last_synced: number | null;
  }> => {
    let res = await this._fetch({
      url: "/pre-orders/number-state",
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Save pre-order number counter to backend (periodic sync)
  saveNumberState = async (
    currentNumber: number,
  ): Promise<{ success: boolean; current_number: number }> => {
    let res = await this._fetch({
      url: "/pre-orders/number-state",
      method: "POST",
      body: { current_number: currentNumber },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get pre-order number configuration (auto-assigned offset)
  getNumberConfig = async (): Promise<{
    offset: number;
    mod: number;
    start_counter: number;
    cache_ttl: number;
    user_id: string;
    company_id: string;
  }> => {
    let res = await this._fetch({
      url: "/pre-orders/number-config",
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Create a manual pre-order
  createManualPreOrder = async (
    partnerId: string,
    orderDate: string,
    deliveryDate: string,
    createInitialFlow: boolean = false,
    documentType: number = 0,
    comment?: string | null,
    status: number = 0,
    preOrderNumber?: string | null,
    id?: string | null, // Optional client-generated UUID for offline-first support
    createdAt?: string | null, // Optional client-generated creation timestamp
    employeeId?: string | null, // Optional employee to assign
    locationId?: string | null, // Optional default location
  ): Promise<PreOrder> => {
    let res = await this._fetch({
      url: "/pre-orders/",
      method: "POST",
      body: {
        ...(id ? { id } : {}), // Include client-provided UUID if available
        ...(createdAt ? { created_at: createdAt } : {}), // Include client timestamp if available
        partner_id: partnerId,
        source_type: 3, // MANUAL
        status: status, // 0=PENDING, 1=CONFIRMED
        type: documentType, // 0=PRE_ORDER, 1=AVOIR
        order_date: orderDate,
        delivery_date: deliveryDate,
        create_initial_flow: createInitialFlow,
        comment: comment ?? null,
        ...(preOrderNumber ? { pre_order_number: preOrderNumber } : {}),
        ...(employeeId ? { employee_id: employeeId } : {}),
        ...(locationId ? { location_id: locationId } : {}),
      },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Create an avoir from a pre-order
  createAvoirFromPreOrder = async (preOrderId: string): Promise<PreOrder> => {
    let res = await this._fetch({
      url: `/pre-orders/${preOrderId}/create-avoir`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Create a purchase order from a pre-order
  createPurchaseFromPreOrder = async (
    preOrderId: string,
  ): Promise<PreOrder> => {
    let res = await this._fetch({
      url: `/pre-orders/${preOrderId}/create-purchase`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Cancel a pre-order (sets status=CANCELLED, cascades to flows)
  cancelPreOrder = async (
    preOrderId: string,
    reason?: string | null,
  ): Promise<PreOrder> => {
    let res = await this._fetch({
      url: `/pre-orders/${preOrderId}/cancel`,
      method: "POST",
      body: { reason: reason ?? null },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      const data = await res.json();
      normalizeFlowDecimals(data);
      return data;
    }
    throw new Error("Bad content type");
  };

  // Delete a pre-order
  deletePreOrder = async (preOrderId: string): Promise<void> => {
    await this._fetch({
      url: `/pre-orders/${preOrderId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
  };

  // Get unread pre-order count for a single chat
  getChatUnreadCount = async (
    chatType: "whatsapp" | "sms" | "email",
    chatId: string,
  ): Promise<{ chat_id: string; chat_type: string; unread_count: number }> => {
    let res = await this._fetch({
      url: `/pre-orders/chat/${chatType}/${chatId}/unread-count`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Batch fetch email IDs linked to pre-orders via junction table
  batchEmailIds = async (
    preOrderIds: string[],
  ): Promise<{ mapping: Record<string, string[]> }> => {
    let res = await this._fetch({
      url: `/pre-orders/batch-email-ids`,
      method: "POST",
      body: { pre_order_ids: preOrderIds },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Batch fetch multiple pre-orders by IDs (optimized for offline sync)
  batchFetch = async (request: BatchFetchRequest): Promise<PreOrder[]> => {
    let res = await this._fetch({
      url: `/pre-orders/batch-fetch`,
      method: "POST",
      body: request,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      const data: PreOrder[] = await res.json();
      data.forEach(normalizeFlowDecimals);
      return data;
    }
    throw new Error("Bad content type");
  };

  // Batch update multiple pre-orders (optimized for offline sync)
  batchUpdate = async (
    request: BatchUpdateRequest,
  ): Promise<BatchUpdateResponse> => {
    let res = await this._fetch({
      url: `/pre-orders/batch-update`,
      method: "POST",
      body: request,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Unified batch operation for simplified offline sync
  unifiedBatch = async (
    request: UnifiedBatchRequest,
  ): Promise<UnifiedBatchResponse> => {
    let res = await this._fetch({
      url: `/pre-orders/unified-batch`,
      method: "POST",
      body: request,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      const response: UnifiedBatchResponse = await res.json();
      // Backend Decimal fields arrive as strings with trailing zeros (e.g. "12.000").
      // Convert them to numbers so the UI never displays unnecessary decimals.
      for (const result of response.results) {
        if (result.data) {
          cleanDecimalStrings(result.data);
          if (Array.isArray(result.data.flows)) {
            for (const flow of result.data.flows) {
              cleanDecimalStrings(flow);
            }
          }
        }
      }
      return response;
    }
    throw new Error("Bad content type");
  };

  // Get change history for a pre-order
  getHistory = async (preOrderId: string): Promise<ChangeAuditItem[]> => {
    let res = await this._fetch({
      url: `/pre-orders/${preOrderId}/history`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get aggregated product totals from all company pre-orders for a date
  getProductTotals = async (
    date: string,
    dateField: "delivery" | "order" | "all" = "delivery",
    start?: string,
    end?: string,
    openEnded?: boolean,
    endDate?: string,
  ): Promise<ProductTotalItem[]> => {
    const params: Record<string, any> = {
      date_field: dateField,
    };
    if (start !== undefined) params.start = start;
    if (end !== undefined) params.end = end;
    if (openEnded) params.open_ended = true;
    if (endDate) params.end_date = endDate;

    let res = await this._fetch({
      url: `/pre-orders/product-totals/${date}`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}

export interface ProductTotalItem {
  product_id: string;
  sku: string;
  code: string;
  name: string;
  short_name: string | null;
  piece_weight: number | null;
  // User's own totals
  my_quantity: number;
  my_weight: number;
  my_flow_count: number;
  // Company-wide totals
  company_quantity: number;
  company_weight: number;
  company_flow_count: number;
}

export interface ChangeAuditItem {
  id: string;
  entity_type: string;
  entity_id: string;
  field_name: string;
  old_value: Record<string, any> | null;
  new_value: Record<string, any> | null;
  old_display: string | null;
  new_display: string | null;
  actor_type: string;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  agent_execution_id: string | null;
  changed_at: string;
  change_source: string | null;
  pre_order_id: string | null;
  flow_context: {
    id: string;
    flow_index: number | null;
    product_id: string | null;
    product_name: string | null;
  } | null;
}

/** Convert Decimal strings from backend (e.g. "12.000") to proper numbers in-place. */
const DECIMAL_FIELDS = [
  "quantity",
  "weight",
  "price_per_weight",
  "total_weight",
  "total_price",
];
function cleanDecimalStrings(obj: Record<string, any>): void {
  for (const key of DECIMAL_FIELDS) {
    if (key in obj && typeof obj[key] === "string") {
      const num = Number(obj[key]);
      if (!isNaN(num)) obj[key] = num;
    }
  }
}

// Unified batch sync types (simplified offline sync)
export interface SyncChange {
  entity_type: "pre-order" | "flow" | "partner";
  entity_id: string;
  operation: "create" | "update" | "delete";
  data: Record<string, any>;
  updated_at: string; // ISO timestamp from client
}

export interface UnifiedBatchRequest {
  changes: SyncChange[];
}

export interface SyncChangeResult {
  entity_type: "pre-order" | "flow" | "partner";
  entity_id: string;
  operation: "create" | "update" | "delete";
  status: "success" | "conflict" | "error";
  error?: string | null;
  server_updated_at?: string | null;
  deleted_at?: string | null; // Soft delete timestamp (dedicated field)
  data?: any;
}

export interface UnifiedBatchResponse {
  results: SyncChangeResult[];
  total_processed: number;
  successful: number;
  conflicts: number;
  errors: number;
}
